import React, { useContext, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Button, Skeleton, Table, Tooltip } from '@mantine/core';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import AppCheckbox from '../../common/AppCheckbox';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import classes from '../../../styles/nestedStyles.module.css';

const GROUP_BY_OPTIONS = [
  { value: 'form', label: 'Form' },
  { value: 'division', label: 'Division' }
];

const RegAssociationDivisionFormAccordionSelect = ({
  pkRegAssociation,
  pkRegAssociationDivisionForms,
  onChange
}) => {
  const { state, fetchRegAssociationResourceOptions } = useContext(
    RegistrationAdminContext
  );
  const [formState, setFormState] = useState({
    activeTab: GROUP_BY_OPTIONS[0].value,
    expandedOptionValues: []
  });

  const { value: options, loading } = state.regAssociationResourceOptions;
  const optionsLoading =
    loading ||
    state.regAssociationResourceOptions.filter?.pkRegAssociation.toString() !==
      pkRegAssociation.toString();

  const selectedGroupByOption = GROUP_BY_OPTIONS.find(
    (v) => v.value === formState.activeTab
  );

  const notSelectedOptions = GROUP_BY_OPTIONS.filter(
    (f) => f.value !== formState.activeTab
  );

  const divisionFormOptions =
    options?.regAssociationDivisions.flatMap((d) =>
      d.regAssociationDivisionForms.map((df) => ({
        ...df,
        regAssociationDivision: d
      }))
    ) || [];

  const displayOptions =
    formState.activeTab === 'form'
      ? options?.regForms
          .map((f) => ({
            value: f.uuid,
            label: f.name,
            subData: divisionFormOptions
              .filter((df) => df.regForm.uuid === f.uuid)
              .map((d) => ({
                value: d.pkRegAssociationDivisionForm,
                label: d.regAssociationDivision.name,
                subLabel: d.regAssociationDivision.regAssociationSeason.name
              }))
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || []
      : options?.regAssociationDivisions
          .map((d) => ({
            value: d.pkRegAssociationDivision,
            label: d.name,
            subLabel: d.regAssociationSeason.name,
            subData: d.regAssociationDivisionForms.map((df) => ({
              value: df.pkRegAssociationDivisionForm,
              label: df.regForm.name
            }))
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const divisionFormCount =
    options?.regAssociationDivisions.reduce(
      (acc, d) => acc + d.regAssociationDivisionForms.length,
      0
    ) || 0;

  const allOptionsSelected =
    !optionsLoading &&
    displayOptions.every((op) =>
      op.subData.every((sd) => pkRegAssociationDivisionForms.includes(sd.value))
    );

  const allOptionsDisabled = false;

  useEffect(() => {
    setFormState({
      activeTab: GROUP_BY_OPTIONS[0].value,
      expandedOptionValues: []
    });
  }, []);

  useEffect(() => {
    if (
      !options ||
      state.regAssociationResourceOptions.filter?.pkRegAssociation?.toString() !==
        pkRegAssociation.toString()
    ) {
      fetchRegAssociationResourceOptions(pkRegAssociation);
    }
  }, [pkRegAssociation]);

  const onChangeSelection = (newSelected) => {
    onChange(
      divisionFormOptions.filter((df) =>
        newSelected.includes(df.pkRegAssociationDivisionForm)
      )
    );
  };

  return (
    <AppStack>
      <AppFlexbox
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <AppFlexbox>
          {!optionsLoading && (
            <AppText style={{ fontSize: 14, color: '#666' }}>
              {pkRegAssociationDivisionForms.length} of {divisionFormCount}{' '}
              forms selected
            </AppText>
          )}
        </AppFlexbox>

        <Button.Group>
          {GROUP_BY_OPTIONS.map((v) => (
            <Button
              key={v.value}
              color="dark"
              onClick={() => {
                setFormState((c) => ({
                  ...c,
                  activeTab: v.value,
                  expandedOptionValues: []
                }));
              }}
              variant={formState.activeTab === v.value ? 'filled' : 'outline'}
            >
              {v.label}
            </Button>
          ))}
        </Button.Group>
      </AppFlexbox>

      <Table
        horizontalSpacing={0}
        style={{
          borderTop: 'solid 1px  #dee2e6',
          width: '100%'
        }}
        verticalSpacing={0}
        withTableBorder
      >
        <Table.Thead style={{ backgroundColor: '#E1E1E1' }}>
          <Table.Tr>
            <Table.Th>
              <AppFlexbox style={{ padding: 8, gap: 8, alignItems: 'center' }}>
                <Tooltip
                  disabled={!allOptionsDisabled}
                  label=""
                  position="bottom"
                  withArrow
                  withinPortal
                >
                  <AppCheckbox
                    checked={!optionsLoading && allOptionsSelected}
                    disabled={optionsLoading || allOptionsDisabled}
                    indeterminate={
                      !allOptionsSelected &&
                      pkRegAssociationDivisionForms.length > 0
                    }
                    onChange={() => {
                      onChangeSelection(
                        allOptionsSelected
                          ? []
                          : displayOptions.flatMap((op) =>
                              op.subData.map((sd) => sd.value)
                            )
                      );
                    }}
                  />
                </Tooltip>

                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  {selectedGroupByOption?.label || 'Option'}
                </AppText>
              </AppFlexbox>
            </Table.Th>

            {notSelectedOptions.map((op) => (
              <Table.Th key={op.value}>
                <AppFlexbox
                  style={{ padding: 8, gap: 8, alignItems: 'center' }}
                >
                  <AppText
                    style={{
                      whiteSpace: 'nowrap',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'end'
                    }}
                  >
                    {op.label}
                  </AppText>
                </AppFlexbox>
              </Table.Th>
            ))}
            <Table.Th>
              <AppFlexbox style={{ padding: 8, gap: 8, alignItems: 'center' }}>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'end'
                  }}
                />
              </AppFlexbox>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {optionsLoading ? (
            <Table.Tr>
              {GROUP_BY_OPTIONS.map((op) => (
                <Table.Td key={op.value}>
                  <AppFlexbox
                    style={{ padding: 8, gap: 8, alignItems: 'center' }}
                  >
                    <Skeleton height={20} width={100} />
                  </AppFlexbox>
                </Table.Td>
              ))}

              <Table.Td />
            </Table.Tr>
          ) : (
            displayOptions.map((op) => {
              const isExpanded = formState.expandedOptionValues.includes(
                op.value
              );

              const hasAnyOptionSelected = op.subData.some((v) =>
                pkRegAssociationDivisionForms.includes(v.value)
              );

              const allGroupOptionsSelected =
                hasAnyOptionSelected &&
                op.subData.every((v) =>
                  pkRegAssociationDivisionForms.includes(v.value)
                );

              return (
                <React.Fragment key={op.value}>
                  <Table.Tr
                    className={classes['hover-action-card']}
                    onClick={() => {
                      setFormState({
                        ...formState,
                        expandedOptionValues: isExpanded
                          ? formState.expandedOptionValues.filter(
                              (v) => v !== op.value
                            )
                          : [...formState.expandedOptionValues, op.value]
                      });
                    }}
                  >
                    <Table.Td>
                      <AppFlexbox
                        style={{
                          padding: '12px 8px',
                          gap: 8,
                          alignItems: 'center'
                        }}
                      >
                        <Tooltip
                          disabled={!allOptionsDisabled}
                          label=""
                          position="bottom"
                          withArrow
                          withinPortal
                        >
                          <AppCheckbox
                            checked={allGroupOptionsSelected}
                            disabled={
                              allOptionsDisabled || op.subData.length === 0
                            }
                            indeterminate={
                              !allGroupOptionsSelected && hasAnyOptionSelected
                            }
                            onChange={(e) => {
                              onChangeSelection(
                                allGroupOptionsSelected
                                  ? pkRegAssociationDivisionForms.filter(
                                      (d) =>
                                        !op.subData.some((sd) => sd.value === d)
                                    )
                                  : [
                                      ...pkRegAssociationDivisionForms.filter(
                                        (d) =>
                                          !op.subData.some(
                                            (sd) => sd.value === d
                                          )
                                      ),
                                      ...op.subData.map((sd) => sd.value)
                                    ]
                              );
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        </Tooltip>

                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              whiteSpace: 'nowrap',
                              fontSize: 14
                            }}
                          >
                            {op.label}
                          </AppText>
                          {op.subLabel && (
                            <AppText
                              style={{
                                whiteSpace: 'nowrap',
                                fontSize: 14,
                                color: '#666'
                              }}
                            >
                              {op.subLabel}
                            </AppText>
                          )}
                        </AppStack>
                      </AppFlexbox>
                    </Table.Td>

                    {notSelectedOptions.map((vop) => (
                      <Table.Td key={vop.value}>
                        <AppFlexbox
                          style={{
                            padding: '12px 8px',
                            flex: 1,
                            color: '#666'
                          }}
                        >
                          {op.subData.length} options
                        </AppFlexbox>
                      </Table.Td>
                    ))}

                    <Table.Td>
                      <AppFlexbox
                        style={{
                          padding: '12px 8px',
                          flex: 1,
                          justifyContent: 'end',
                          color: '#000',
                          textDecoration: 'none'
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </AppFlexbox>
                    </Table.Td>
                  </Table.Tr>

                  {isExpanded &&
                    op.subData.map((sd, index) => {
                      const subDataSelected = pkRegAssociationDivisionForms.includes(
                        sd.value
                      );

                      const subDataDisabled = false;

                      return (
                        <Table.Tr
                          key={sd.value}
                          style={
                            index === op.subData.length - 1
                              ? {}
                              : { border: 'none' }
                          }
                        >
                          <Table.Td style={{ border: 'none' }}>
                            <AppFlexbox
                              style={{
                                padding: 8,
                                gap: 8,
                                alignItems: 'center'
                              }}
                            >
                              <Tooltip
                                disabled={!subDataDisabled}
                                label="Variant is already assigned to a design"
                                position="bottom"
                                withArrow
                                withinPortal
                              >
                                <AppCheckbox
                                  checked={subDataSelected}
                                  disabled={subDataDisabled}
                                  onChange={(e) => {
                                    onChangeSelection(
                                      subDataSelected
                                        ? pkRegAssociationDivisionForms.filter(
                                            (d) => d !== sd.value
                                          )
                                        : [
                                            ...pkRegAssociationDivisionForms,
                                            sd.value
                                          ]
                                    );
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </Tooltip>
                            </AppFlexbox>
                          </Table.Td>

                          {notSelectedOptions.map((vop) => (
                            <Table.Td key={vop.value}>
                              <AppFlexbox
                                style={{
                                  padding: 8,
                                  gap: 8,
                                  flex: 1,
                                  alignItems: 'center'
                                }}
                              >
                                <AppStack style={{ gap: 0 }}>
                                  <AppText style={{ fontSize: 14 }}>
                                    {sd.label || '-'}
                                  </AppText>
                                  {sd.subLabel && (
                                    <AppText
                                      style={{ fontSize: 14, color: '#666' }}
                                    >
                                      {sd.subLabel || '-'}
                                    </AppText>
                                  )}
                                </AppStack>
                              </AppFlexbox>
                            </Table.Td>
                          ))}

                          <Table.Td style={{ border: 'none' }}>
                            <AppFlexbox
                              style={{
                                padding: 8,
                                flex: 1,
                                justifyContent: 'end',
                                color: '#000',
                                textDecoration: 'none'
                              }}
                            >
                              <></>
                            </AppFlexbox>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                </React.Fragment>
              );
            })
          )}
        </Table.Tbody>
      </Table>
    </AppStack>
  );
};

RegAssociationDivisionFormAccordionSelect.propTypes = {
  onChange: PropTypes.func,
  pkRegAssociation: PropTypes.number,
  pkRegAssociationDivisionForms: PropTypes.arrayOf(PropTypes.number)
};

export default RegAssociationDivisionFormAccordionSelect;
